import React, { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import { getSelectionHistory, HistorySuggestion, SelectionHistoryResponse, SelectionRecord, SelectionStatus } from '../services/selectionHistoryService';
import '../styles/Dashboard.css';
import '../styles/SelectionHistory.css';

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('tr-TR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

function SuggestionCard({ suggestion, isSelected, selectedAt, fallback = false }: {
  suggestion: HistorySuggestion; isSelected: boolean; selectedAt: string | null; fallback?: boolean;
}) {
  return (
    <article className={'selection-suggestion-card' + (isSelected ? ' selection-suggestion-card--selected' : '')}
      aria-label={(suggestion.rank ? suggestion.rank + '. öneri' : 'Öneri') + (isSelected ? ', kullanıcı seçimi' : '')}>
      <div className="selection-suggestion-heading">
        <span className="selection-suggestion-rank">{suggestion.rank ? suggestion.rank + '. öneri' : 'Seçilen öneri'}</span>
        {isSelected && <span className="selection-status selection-status--selected">✓ Kullanıcının seçimi</span>}
      </div>
      {fallback && <p className="selection-archive-note">Dönen cevabın ayrıntıları saklanmamış. Kayıttaki seçim bilgileri gösteriliyor.</p>}
      <h4 className="selection-suggestion-description">{suggestion.description || 'Öneri açıklaması bu kayıtta bulunmuyor.'}</h4>
      <dl>
        <div><dt>Ana grup</dt><dd>{suggestion.parentGroup || '—'}</dd></div>
        <div><dt>Grup</dt><dd>{suggestion.group || '—'}</dd></div>
        <div><dt>Güven puanı</dt><dd>{suggestion.confidence === null ? '—' : '%' + (suggestion.confidence * 100).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}</dd></div>
        {isSelected && <div><dt>Seçim zamanı</dt><dd>{formatDate(selectedAt)}</dd></div>}
      </dl>
      <h5>Etiketler</h5>
      <div className="selection-tags">{suggestion.tags.length ? suggestion.tags.map((tag, index) => <span key={index}>{tag}</span>) : 'Etiket bulunmuyor.'}</div>
      <h5>Çözüm önerisi</h5>
      <p className="selection-full-text">{suggestion.solution || 'Çözüm önerisi bulunmuyor.'}</p>
    </article>
  );
}

function ResponsePreview({ record }: { record: SelectionRecord }) {
  const first = record.suggestions[0];
  if (!first) return <span className="selection-muted">{record.responseStatus === 'empty' ? 'Öneri bulunamadı' : 'Cevap ayrıntıları bulunmuyor'}</span>;
  return <>
    <span className="selection-response-count">{record.suggestionCount} öneri döndü</span>
    <p className="selection-preview">{first.description || 'Öneri açıklaması bulunmuyor.'}</p>
    <p className="selection-group">{first.parentGroup || '—'} / {first.group || '—'}</p>
    {record.selected && <p className="selection-chosen-preview">✓ {record.selected.rank ? record.selected.rank + '. öneri seçildi' : 'Seçim yapıldı'}
      {record.selected.id !== first.id && <span>{record.selected.description || record.selected.group || 'Seçim ayrıntılarını inceleyin.'}</span>}
    </p>}
  </>;
}

function SelectionDetails({ record }: { record: SelectionRecord }) {
  const selectedInResponse = record.suggestions.some(suggestion => suggestion.isSelected);
  return (
    <div className="selection-details">
      <section className="selection-query-card">
        <div><h3>Girilen hata açıklaması</h3><p className="selection-full-text">{record.description || 'Açıklama bulunmuyor.'}</p></div>
        <dl>
          <div><dt>Sorgu zamanı</dt><dd>{formatDate(record.generatedAt)}</dd></div>
          <div><dt>Kullanıcı seçimi</dt><dd>{record.selected ? (record.selected.rank ? record.selected.rank + '. öneri seçildi' : 'Seçim yapıldı') : 'Seçim yapılmadı'}</dd></div>
        </dl>
      </section>
      <section className="selection-responses" aria-label="Dönen öneriler">
        <div className="selection-responses-heading">
          <div><h3>Dönen öneriler {record.responseStatus === 'available' && <span>({record.suggestionCount})</span>}</h3>
            <p>Bu hata açıklaması için sorgu sırasında sunulan cevaplar.</p></div>
          {record.responseStatus === 'available' && !record.selected && <span className="selection-status selection-status--pending">Henüz seçim yapılmadı</span>}
        </div>
        {record.responseStatus === 'empty' && <div className="selection-response-empty"><strong>Bu açıklama için öneri bulunamadı.</strong><p>Sistem sorguya boş bir öneri listesi döndürmüş.</p></div>}
        {record.responseStatus === 'unavailable' && <div className="selection-response-empty"><strong>Bu sorgunun cevap ayrıntıları saklanmamış.</strong><p>O sırada sunulan öneriler bu kayıt için görüntülenemiyor.</p></div>}
        <div className="selection-suggestion-list">
          {record.suggestions.map((suggestion, index) => <SuggestionCard key={suggestion.id + '-' + index} suggestion={suggestion} isSelected={suggestion.isSelected} selectedAt={record.selectedAt} />)}
          {record.selected && !selectedInResponse && <SuggestionCard suggestion={record.selected} isSelected selectedAt={record.selectedAt} fallback />}
        </div>
      </section>
    </div>
  );
}

const SelectionHistoryPage: React.FC = () => {
  const [filters, setFilters] = useState({ search: '', status: 'all' as SelectionStatus, date: '', page: 1, limit: 25 });
  const [searchInput, setSearchInput] = useState('');
  const [result, setResult] = useState<SelectionHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters(previous => previous.search === searchInput.trim() ? previous : { ...previous, search: searchInput.trim(), page: 1 });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setResult(null);
    setExpandedId(null);
    getSelectionHistory(filters, controller.signal)
      .then(data => { if (!controller.signal.aborted) setResult(data); })
      .catch(() => { if (!controller.signal.aborted) setError('Kayıtlar yüklenemedi. Lütfen bağlantınızı kontrol edip yeniden deneyin.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [filters, refresh]);

  const resetFilters = () => {
    setSearchInput('');
    setFilters(previous => ({ ...previous, search: '', status: 'all', date: '', page: 1 }));
  };
  const records = result?.data || [];
  const selectedCount = records.filter(record => record.status === 'selected').length;
  const hasFilters = Boolean(searchInput || filters.status !== 'all' || filters.date);

  return (
    <div className="container selection-page">
      <Navigation />
      <header className="selection-header">
        <div>
          <span className="selection-eyebrow">Sorgudan seçime</span>
          <h1>Hata Açıklamaları ve Seçimler</h1>
          <p>Girilen hata açıklamalarını, dönen cevapları ve kullanıcıların seçimlerini birlikte inceleyin.</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => setRefresh(value => value + 1)} disabled={loading}>↻ Yenile</button>
      </header>

      <section className="selection-filters" aria-label="Kayıt filtreleri">
        <label className="selection-search">Hata açıklamasında ara
          <input type="search" value={searchInput} maxLength={300} placeholder="Örn. kapı fitili, boya çizik…"
            onChange={event => setSearchInput(event.target.value)} />
        </label>
        <label>Seçim durumu
          <select value={filters.status} onChange={event => setFilters(previous => ({ ...previous, status: event.target.value as SelectionStatus, page: 1 }))}>
            <option value="all">Tüm kayıtlar</option>
            <option value="selected">Seçim yapılanlar</option>
            <option value="pending">Seçim yapılmayanlar</option>
          </select>
        </label>
        <label>Sorgu tarihi
          <input type="date" value={filters.date} onChange={event => setFilters(previous => ({ ...previous, date: event.target.value, page: 1 }))} />
        </label>
        {hasFilters && <button type="button" className="btn btn-secondary" onClick={resetFilters}>Filtreleri temizle</button>}
      </section>

      <section className="selection-results" aria-label="Hata açıklamaları ve seçim kayıtları" aria-busy={loading}>
        <div className="selection-results-header">
          <div><h2>Kayıtlar</h2><p>21 Eylül 2026’dan itibaren yapılan sorgular. Tüm cevapları görmek için kaydı açın.</p></div>
          {result && <div className="selection-counts" aria-label="Bu sayfadaki kayıt sayıları">
            <span>Bu sayfada <strong>{records.length}</strong> kayıt</span>
            <span className="selection-count-selected">{selectedCount} seçim yapıldı</span>
            <span>{records.length - selectedCount} seçim yapılmadı</span>
          </div>}
        </div>
        {loading && <div className="loading" role="status"><div className="spinner" /><p>Kayıtlar yükleniyor…</p></div>}
        {error && <div className="selection-empty" role="alert"><h3>Kayıtlar alınamadı</h3><p>{error}</p>
          <button type="button" className="btn btn-primary" onClick={() => setRefresh(value => value + 1)}>Yeniden dene</button>
        </div>}
        {!loading && !error && result && records.length === 0 && <div className="selection-empty">
          <h3>{hasFilters ? 'Filtrelere uygun kayıt bulunamadı' : 'Henüz kayıt bulunmuyor'}</h3>
          <p>{hasFilters ? 'Farklı bir açıklama, tarih veya seçim durumu deneyin.' : 'Hata açıklamasıyla yapılan sorgular ve ardından seçilen öneriler burada görünecek.'}</p>
          {hasFilters && <button type="button" className="btn btn-secondary" onClick={resetFilters}>Filtreleri temizle</button>}
        </div>}
        {!loading && !error && records.length > 0 && <div className="selection-table-wrap">
          <table className="selection-table">
            <thead><tr><th scope="col">Hata açıklaması</th><th scope="col">Dönen cevaplar</th><th scope="col">Durum</th><th scope="col">Sorgu zamanı</th><th scope="col">Ayrıntı</th></tr></thead>
            <tbody>{records.map(record => <React.Fragment key={record.id}>
              <tr>
                <td data-label="Hata açıklaması"><p className="selection-preview">{record.description || 'Açıklama bulunmuyor.'}</p></td>
                <td data-label="Dönen cevaplar"><ResponsePreview record={record} /></td>
                <td data-label="Durum"><span className={'selection-status selection-status--' + (record.status === 'pending' && record.responseStatus === 'empty' ? 'empty' : record.status)}>{record.status === 'selected' ? '✓ Seçim yapıldı' : record.responseStatus === 'empty' ? 'Öneri yok' : 'Seçim yapılmadı'}</span></td>
                <td data-label="Sorgu zamanı" className="selection-date">{formatDate(record.generatedAt)}</td>
                <td className="selection-action-cell"><button type="button" className="selection-detail-button" aria-expanded={expandedId === record.id} aria-controls={'selection-' + record.id}
                  aria-label={(expandedId === record.id ? 'Ayrıntıları kapat: ' : 'Ayrıntıları göster: ') + record.description}
                  onClick={() => setExpandedId(previous => previous === record.id ? null : record.id)}>{expandedId === record.id ? 'Kapat ↑' : 'İncele ↓'}</button></td>
              </tr>
              {expandedId === record.id && <tr className="selection-detail-row"><td colSpan={5} id={'selection-' + record.id}><SelectionDetails record={record} /></td></tr>}
            </React.Fragment>)}</tbody>
          </table>
        </div>}
        {result && !loading && !error && <div className="selection-pagination">
          <button type="button" className="btn btn-secondary" disabled={!result.pagination.hasPrevPage} onClick={() => setFilters(previous => ({ ...previous, page: previous.page - 1 }))}>← Önceki</button>
          <div><span>Sayfa {result.pagination.currentPage}</span>
            <select aria-label="Sayfa başına kayıt sayısı" value={filters.limit} onChange={event => setFilters(previous => ({ ...previous, limit: Number(event.target.value), page: 1 }))}>
              {[10, 25, 50, 100].map(size => <option key={size} value={size}>Sayfa başına {size}</option>)}
            </select>
          </div>
          <button type="button" className="btn btn-secondary" disabled={!result.pagination.hasNextPage || filters.page >= 5000} onClick={() => setFilters(previous => ({ ...previous, page: previous.page + 1 }))}>Sonraki →</button>
        </div>}
      </section>
    </div>
  );
};

export default SelectionHistoryPage;
